/**
 * Error Factory - Creates properly typed errors
 *
 * @see /docs/ERROR_BOUNDARY_IMPROVEMENTS.md for usage patterns
 */
import type { ErrorResponseDto } from '@splitfiesta/shared';

import { ErrorCategory, ErrorSeverity } from '@/types/errors';
import type {
  ApiError,
  ApplicationError,
  AuthError,
  ChunkLoadError,
  NetworkError,
  RuntimeError,
  ValidationError,
} from '@/types/errors';

export class ErrorFactory {
  /**
   * Create a network error
   *
   * @param message - Human-readable error message
   * @param details - Network request details
   * @returns Immutable NetworkError with type-safe properties
   *
   * @example
   * ```typescript
   * // Basic network error
   * const error = ErrorFactory.createNetworkError('Connection timeout');
   *
   * // With full details
   * const error = ErrorFactory.createNetworkError('Request failed', {
   *   status: 500,
   *   statusText: 'Internal Server Error',
   *   url: 'https://api.example.com/expenses',
   *   method: 'POST',
   *   retryable: true
   * });
   * ```
   */
  static createNetworkError(
    message: string,
    details: {
      status?: number;
      statusText?: string;
      url?: string;
      method?: string;
      retryable?: boolean;
    } = {}
  ): NetworkError {
    const error = new Error(message) as NetworkError;

    // Make properties readonly via Object.defineProperties
    Object.defineProperties(error, {
      category: {
        value: ErrorCategory.NETWORK,
        writable: false,
        enumerable: true,
      },
      severity: {
        value: ErrorSeverity.HIGH,
        writable: false,
        enumerable: true,
      },
      timestamp: {
        value: new Date(),
        writable: false,
        enumerable: true,
      },
      retryable: {
        value: details.retryable ?? true,
        writable: false,
        enumerable: true,
      },
      status: {
        value: details.status,
        writable: false,
        enumerable: true,
      },
      statusText: {
        value: details.statusText,
        writable: false,
        enumerable: true,
      },
      url: {
        value: details.url,
        writable: false,
        enumerable: true,
      },
      method: {
        value: details.method,
        writable: false,
        enumerable: true,
      },
    });

    return error;
  }

  /**
   * Create an API error from backend ErrorResponseDto
   *
   * @param response - Error response from backend API
   * @param originalRequest - Optional request details that caused the error
   * @returns Immutable ApiError with backend error data
   *
   * @example
   * ```typescript
   * // From API response
   * const response = {
   *   success: false,
   *   error: {
   *     code: 'INVALID_INPUT',
   *     message: 'Invalid expense amount',
   *     translationKey: 'errors.validation.invalidAmount',
   *     translationParams: { field: 'amount', min: 0 }
   *   }
   * };
   *
   * const error = ErrorFactory.createApiError(response, {
   *   endpoint: '/api/expenses',
   *   method: 'POST',
   *   body: { amount: -100 },
   *   status: 400
   * });
   * ```
   */
  static createApiError(
    response: ErrorResponseDto,
    originalRequest?: {
      endpoint: string;
      method: string;
      body?: unknown;
      status?: number;
    }
  ): ApiError {
    const message = response.error.message;
    const error = new Error(message) as ApiError;

    Object.defineProperties(error, {
      category: {
        value: ErrorCategory.NETWORK,
        writable: false,
        enumerable: true,
      },
      severity: {
        value: ErrorSeverity.HIGH,
        writable: false,
        enumerable: true,
      },
      timestamp: {
        value: new Date(),
        writable: false,
        enumerable: true,
      },
      retryable: {
        value: response.error.code !== 'UNAUTHORIZED',
        writable: false,
        enumerable: true,
      },
      code: {
        value: response.error.code,
        writable: false,
        enumerable: true,
      },
      translationKey: {
        value: response.error.translationKey,
        writable: false,
        enumerable: true,
      },
      translationParams: {
        value: response.error.translationParams,
        writable: false,
        enumerable: true,
      },
      response: {
        value: response,
        writable: false,
        enumerable: true,
      },
      originalRequest: {
        value: originalRequest,
        writable: false,
        enumerable: true,
      },
      status: {
        value: originalRequest?.status,
        writable: false,
        enumerable: true,
      },
    });

    return error;
  }

  /**
   * Create a validation error
   *
   * @param message - Human-readable validation error message
   * @param details - Optional validation details (field errors, form data, translation key)
   * @returns Immutable ValidationError with field-level error details
   *
   * @example
   * ```typescript
   * // Simple validation error
   * const error = ErrorFactory.createValidationError('Form validation failed');
   *
   * // With field errors
   * const error = ErrorFactory.createValidationError('Validation failed', {
   *   fields: {
   *     email: ['Invalid email format', 'Email already exists'],
   *     password: ['Password must be at least 8 characters']
   *   },
   *   translationKey: 'errors.validation.failed'
   * });
   * ```
   */
  static createValidationError(
    message: string,
    details?: {
      fields?: Record<string, string[]>;
      formData?: Record<string, unknown>;
      translationKey?: string;
    }
  ): ValidationError {
    const error = new Error(message) as ValidationError;

    Object.defineProperties(error, {
      category: {
        value: ErrorCategory.VALIDATION,
        writable: false,
        enumerable: true,
      },
      severity: {
        value: ErrorSeverity.MEDIUM,
        writable: false,
        enumerable: true,
      },
      timestamp: {
        value: new Date(),
        writable: false,
        enumerable: true,
      },
      retryable: {
        value: false,
        writable: false,
        enumerable: true,
      },
      fields: {
        value: details?.fields,
        writable: false,
        enumerable: true,
      },
      formData: {
        value: details?.formData,
        writable: false,
        enumerable: true,
      },
      translationKey: {
        value: details?.translationKey,
        writable: false,
        enumerable: true,
      },
    });

    return error;
  }

  /**
   * Create an authentication/authorization error
   *
   * @param message - Human-readable auth error message
   * @param type - Type of auth error ('authentication' or 'authorization')
   * @param details - Optional auth details (redirect URL, required permission, translation key)
   * @returns Immutable AuthError with auth-specific details
   *
   * @example
   * ```typescript
   * // Authentication error (login required)
   * const error = ErrorFactory.createAuthError(
   *   'Invalid credentials',
   *   'authentication',
   *   {
   *     redirectTo: '/login',
   *     translationKey: 'errors.auth.invalidCredentials'
   *   }
   * );
   *
   * // Authorization error (insufficient permissions)
   * const error = ErrorFactory.createAuthError(
   *   'Insufficient permissions',
   *   'authorization',
   *   {
   *     requiredPermission: 'groups:admin',
   *     translationKey: 'errors.auth.forbidden'
   *   }
   * );
   * ```
   */
  static createAuthError(
    message: string,
    type: 'authentication' | 'authorization',
    details?: {
      redirectTo?: string;
      requiredPermission?: string;
      translationKey?: string;
    }
  ): AuthError {
    const error = new Error(message) as AuthError;

    Object.defineProperties(error, {
      category: {
        value:
          type === 'authentication'
            ? ErrorCategory.AUTHENTICATION
            : ErrorCategory.AUTHORIZATION,
        writable: false,
        enumerable: true,
      },
      severity: {
        value: ErrorSeverity.HIGH,
        writable: false,
        enumerable: true,
      },
      timestamp: {
        value: new Date(),
        writable: false,
        enumerable: true,
      },
      retryable: {
        value: false,
        writable: false,
        enumerable: true,
      },
      redirectTo: {
        value: details?.redirectTo,
        writable: false,
        enumerable: true,
      },
      requiredPermission: {
        value: details?.requiredPermission,
        writable: false,
        enumerable: true,
      },
      translationKey: {
        value: details?.translationKey,
        writable: false,
        enumerable: true,
      },
    });

    return error;
  }

  /**
   * Create a chunk load error (code splitting failure)
   *
   * @param message - Human-readable chunk load error message
   * @param details - Optional chunk details (chunk ID, module path)
   * @returns Immutable ChunkLoadError for lazy-loaded module failures
   *
   * @example
   * ```typescript
   * // Basic chunk load error
   * const error = ErrorFactory.createChunkLoadError(
   *   'Failed to load chunk: /static/js/3.chunk.js'
   * );
   *
   * // With chunk details
   * const error = ErrorFactory.createChunkLoadError(
   *   'Chunk load failed',
   *   {
   *     chunkId: '3',
   *     modulePath: '/static/js/3.chunk.js'
   *   }
   * );
   * ```
   */
  static createChunkLoadError(
    message: string,
    details?: {
      chunkId?: string;
      modulePath?: string;
    }
  ): ChunkLoadError {
    const error = new Error(message) as ChunkLoadError;

    Object.defineProperties(error, {
      category: {
        value: ErrorCategory.CHUNK_LOAD,
        writable: false,
        enumerable: true,
      },
      severity: {
        value: ErrorSeverity.HIGH,
        writable: false,
        enumerable: true,
      },
      timestamp: {
        value: new Date(),
        writable: false,
        enumerable: true,
      },
      retryable: {
        value: true,
        writable: false,
        enumerable: true,
      },
      chunkId: {
        value: details?.chunkId,
        writable: false,
        enumerable: true,
      },
      modulePath: {
        value: details?.modulePath,
        writable: false,
        enumerable: true,
      },
    });

    return error;
  }

  /**
   * Create a runtime error (generic JavaScript error)
   *
   * @param originalError - The original JavaScript Error object
   * @param details - Optional runtime details (component stack from React)
   * @returns Immutable RuntimeError with preserved stack trace
   *
   * @example
   * ```typescript
   * // Basic runtime error
   * try {
   *   // some code
   * } catch (err) {
   *   const error = ErrorFactory.createRuntimeError(err as Error);
   * }
   *
   * // With React component stack
   * const error = ErrorFactory.createRuntimeError(originalError, {
   *   componentStack: errorInfo.componentStack
   * });
   * ```
   */
  static createRuntimeError(
    originalError: Error,
    details?: {
      componentStack?: string;
    }
  ): RuntimeError {
    const error = new Error(originalError.message) as RuntimeError;

    // Copy stack trace
    error.stack = originalError.stack;

    Object.defineProperties(error, {
      category: {
        value: ErrorCategory.RUNTIME,
        writable: false,
        enumerable: true,
      },
      severity: {
        value: ErrorSeverity.HIGH,
        writable: false,
        enumerable: true,
      },
      timestamp: {
        value: new Date(),
        writable: false,
        enumerable: true,
      },
      retryable: {
        value: false,
        writable: false,
        enumerable: true,
      },
      componentStack: {
        value: details?.componentStack,
        writable: false,
        enumerable: true,
      },
    });

    return error;
  }

  /**
   * Normalize any error to an ApplicationError
   *
   * Converts unknown error types (strings, objects, Error instances) into
   * properly typed ApplicationError objects. Attempts to detect error type
   * from error message patterns.
   *
   * @param error - Any error value (Error, string, object, etc.)
   * @returns Type-safe ApplicationError
   *
   * @example
   * ```typescript
   * // Normalize caught exceptions
   * try {
   *   await someAsyncOperation();
   * } catch (err) {
   *   const normalizedError = ErrorFactory.normalize(err);
   *   await logError(normalizedError);
   * }
   *
   * // Handles various error types
   * ErrorFactory.normalize(new Error('Failed'));           // → RuntimeError
   * ErrorFactory.normalize('Something went wrong');        // → RuntimeError
   * ErrorFactory.normalize({ message: 'Error' });          // → RuntimeError
   * ErrorFactory.normalize(new Error('Loading chunk 5')); // → ChunkLoadError
   * ```
   */
  static normalize(error: unknown): ApplicationError {
    if (error instanceof Error) {
      // Check for specific error patterns
      const errorMsg = error.message.toLowerCase();

      if (errorMsg.includes('chunk') || errorMsg.includes('loading chunk')) {
        return this.createChunkLoadError(error.message);
      }

      if (errorMsg.includes('network') || errorMsg.includes('fetch')) {
        return this.createNetworkError(error.message, {});
      }

      // Default to runtime error
      return this.createRuntimeError(error);
    }

    // Non-Error object
    return this.createRuntimeError(new Error(String(error)));
  }
}
